import { getApiUrl } from './api';

export interface UserRegistration {
  email: string;
  password: string;
  username: string;
  full_name?: string;
  h_captcha_token?: string;
}

export interface UserLogin {
  email: string;
  password: string;
}

export interface UserUpdate {
  email?: string | null;
  password?: string | null;
  full_name?: string | null;
  phone_number?: string | null;
}

export interface AddressCreate {
  label?: string | null;
  name: string;
  line1: string;
  line2?: string | null;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  phone_number?: string | null;
  is_default_shipping?: boolean;
  is_default_billing?: boolean;
}

export interface AddressUpdate {
  label?: string | null;
  name?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  phone_number?: string | null;
  is_default_shipping?: boolean | null;
  is_default_billing?: boolean | null;
}

export interface UserData {
  id: number;
  email: string;
  full_name?: string;
  is_active: boolean;
  is_verified: boolean;
  is_superuser: boolean;
  registered_at: string;
  addresses: Address[];
}

export interface Address {
  id: number;
  label: string | null;
  name: string;
  line1: string;
  line2?: string | null;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  phone_number?: string | null;
  is_default_shipping: boolean;
  is_default_billing: boolean;
  created_at: string;
  updated_at: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
}

// Register a new user
export const registerUser = async (userData: {
  email: string;
  password: string;
  full_name?: string;
  username: string;
  h_captcha_token: string;
}) => {
  try {
    const requestBody = {
      email: userData.email,
      password: userData.password,
      full_name: userData.full_name,
      h_captcha_token: userData.h_captcha_token,
    };
    
    console.log('Registration request body:', JSON.stringify(requestBody));
    
    const response = await fetch(`${getApiUrl()}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    const responseText = await response.text();
    console.log('Registration response status:', response.status);
    console.log('Registration response headers:', Object.fromEntries(Array.from(response.headers)));
    console.log('Registration response text:', responseText);
    
    if (!response.ok) {
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch (e) {
        errorData = { detail: responseText };
      }
      throw new Error(errorData.detail || `Registration failed with status: ${response.status}`);
    }

    return responseText ? JSON.parse(responseText) : {};
  } catch (error) {
    throw new Error(`Registration failed: ${error instanceof Error ? error.message : String(error)}`);
  }
};

// Login a user with JWT
export const loginUser = async (credentials: UserLogin): Promise<TokenResponse> => {
  const formData = new URLSearchParams();
  formData.append('email', credentials.email);
  formData.append('username', credentials.email);
  formData.append('password', credentials.password);

  const response = await fetch(`${getApiUrl()}/auth/jwt/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || `Login failed with status: ${response.status}`);
  }

  return response.json();
};

// Login a user with Cookie
export const loginUserWithCookie = async (credentials: UserLogin): Promise<void> => {
  const formData = new URLSearchParams();
  formData.append('email', credentials.email);
  formData.append('username', credentials.email);
  formData.append('password', credentials.password);

  const response = await fetch(`${getApiUrl()}/auth/cookie/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData,
    credentials: 'include', // Important for cookies
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || `Login failed with status: ${response.status}`);
  }
};

// Logout a user (JWT)
export const logoutUser = async (token: string): Promise<void> => {
  const response = await fetch(`${getApiUrl()}/auth/jwt/logout`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Logout failed with status: ${response.status}`);
  }
};

// Logout a user (Cookie)
export const logoutUserCookie = async (): Promise<void> => {
  const response = await fetch(`${getApiUrl()}/auth/cookie/logout`, {
    method: 'POST',
    credentials: 'include', // Important for cookies
  });

  if (!response.ok) {
    throw new Error(`Logout failed with status: ${response.status}`);
  }
};

// Get current user info
export const getCurrentUser = async (token: string): Promise<UserData> => {
  const response = await fetch(`${getApiUrl()}/users/me`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to get user info with status: ${response.status}`);
  }

  return response.json();
};

// Update current user
export const updateCurrentUser = async (token: string, userData: UserUpdate): Promise<UserData> => {
  const response = await fetch(`${getApiUrl()}/users/me`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || `Update failed with status: ${response.status}`);
  }

  return response.json();
};

// Request password reset
export const requestPasswordReset = async (email: string, h_captcha_token?: string): Promise<void> => {
  const requestBody: any = { email };
  if (h_captcha_token) requestBody.h_captcha_token = h_captcha_token;
  
  const response = await fetch(`${getApiUrl()}/auth/forgot-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const responseText = await response.text();
    let errorData;
    try {
      errorData = JSON.parse(responseText);
    } catch (e) {
      errorData = { detail: responseText };
    }
    throw new Error(errorData.detail || `Password reset request failed with status: ${response.status}`);
  }
};

// Reset password
export const resetPassword = async (token: string, password: string): Promise<void> => {
  const response = await fetch(`${getApiUrl()}/auth/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token, password }),
  });

  if (!response.ok) {
    const responseText = await response.text();
    let errorData;
    try {
      errorData = JSON.parse(responseText);
    } catch (e) {
      errorData = { detail: responseText };
    }
    throw new Error(errorData.detail || `Password reset failed with status: ${response.status}`);
  }
};

// Request email verification
export const requestVerification = async (email: string, h_captcha_token: string): Promise<void> => {
  const response = await fetch(`${getApiUrl()}/auth/request-verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, h_captcha_token }),
  });

  if (!response.ok) {
    const responseText = await response.text();
    let errorData;
    try {
      errorData = JSON.parse(responseText);
    } catch (e) {
      errorData = { detail: responseText };
    }
    throw new Error(errorData.detail || `Verification request failed with status: ${response.status}`);
  }
};

// Verify email
export const verifyEmail = async (token: string): Promise<UserData> => {
  const response = await fetch(`${getApiUrl()}/auth/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || `Email verification failed with status: ${response.status}`);
  }

  return response.json();
};

// Get user's addresses
export const getUserAddresses = async (token: string): Promise<Address[]> => {
  const response = await fetch(`${getApiUrl()}/addresses/`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to get addresses with status: ${response.status}`);
  }

  return response.json();
};

// Create a new address
export const createAddress = async (token: string, addressData: AddressCreate): Promise<Address> => {
  const response = await fetch(`${getApiUrl()}/addresses/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(addressData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || `Address creation failed with status: ${response.status}`);
  }

  return response.json();
};

// Update an address
export const updateAddress = async (token: string, addressId: number, addressData: AddressUpdate): Promise<Address> => {
  const response = await fetch(`${getApiUrl()}/addresses/${addressId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(addressData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || `Address update failed with status: ${response.status}`);
  }

  return response.json();
};

// Delete an address
export const deleteAddress = async (token: string, addressId: number): Promise<void> => {
  const response = await fetch(`${getApiUrl()}/addresses/${addressId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Address deletion failed with status: ${response.status}`);
  }
};

// Set default shipping address
export const setDefaultShippingAddress = async (token: string, addressId: number): Promise<Address> => {
  const response = await fetch(`${getApiUrl()}/addresses/${addressId}/set-default-shipping`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to set default shipping address with status: ${response.status}`);
  }

  return response.json();
};

// Set default billing address
export const setDefaultBillingAddress = async (token: string, addressId: number): Promise<Address> => {
  const response = await fetch(`${getApiUrl()}/addresses/${addressId}/set-default-billing`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to set default billing address with status: ${response.status}`);
  }

  return response.json();
}; 